// src/oura/hooks/useGoogleAiRefresh.ts
//
// Reading Google's AI Overviews on demand (#3922, ourteam-backend#5037/#5067).
//
// This is a sibling of `useRefreshJob`, not a use of it, because the thing that
// makes it safe is different. The stage re-run remembers its job in
// localStorage; THIS one asks the server what is running, on every mount. That
// matters here and not there: a read types fifty questions into a browser over
// a quarter of an hour and spends proxy bandwidth doing it, so a reload, a
// second tab, or a second staffer opening the same business must join the read
// in flight rather than start a second one. localStorage cannot know about the
// other staffer.
//
// The in-flight state is sticky for the same reason it is everywhere else in
// this epic: a staffer who clicks again must not be able to lose it.

import { useCallback, useEffect, useRef, useState } from "react";
import {
    getGoogleAiRefreshJob,
    getGoogleAiRefreshState,
    startGoogleAiRefresh,
    type AeoEngineShare,
} from "@service/aeo";

/** Five seconds, for twenty-five minutes. A 50-question read runs 8 to 15
 *  minutes, and a job the server lost comes back `failed` with `stale` at 20 —
 *  so the client must still be watching when that arrives. */
const POLL_MS = 5_000;
const POLL_LIMIT = 300;

export interface GoogleAiRefresh {
    running: boolean;
    /**
     * The POST is in flight — the moment between pressing and knowing.
     *
     * Separate from `running` because it is the only part the person is
     * watching for: it lasts a few hundred milliseconds, and without it the
     * dialog closed on the press and whatever happened next appeared somewhere
     * else on the card. A press that produces no visible response is a press
     * people repeat.
     */
    starting: boolean;
    note: { tone: "info" | "warn"; text: string } | null;
    /** No route here, or not a staff session: render no control at all. */
    absent: boolean;
    /** The control cannot work, and this is the backend's reason. Disabled,
     *  not hidden — a staffer who cannot use it deserves to know why. */
    unavailable: string | null;
    /** The row the read landed, shown before any refetch arrives. */
    row: AeoEngineShare | null;
    confirmOpen: boolean;
    ask: () => void;
    cancel: () => void;
    run: () => Promise<void>;
}

export function useGoogleAiRefresh({
    businessId,
    enabled,
    onSettled,
}: {
    businessId?: string;
    /** Staff, live business, flag on. False renders nothing and asks nothing. */
    enabled: boolean;
    /** Re-read the business once the row lands, so the rest of the screen
     *  agrees with the row just written. */
    onSettled?: () => void | Promise<void>;
}): GoogleAiRefresh {
    const [running, setRunning] = useState(false);
    const [note, setNote] = useState<GoogleAiRefresh["note"]>(null);
    const [absent, setAbsent] = useState(false);
    const [unavailable, setUnavailable] = useState<string | null>(null);
    const [row, setRow] = useState<AeoEngineShare | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [starting, setStarting] = useState(false);

    const aliveRef = useRef(true);
    // State would be a frame behind a fast double-press; a ref is not.
    const runningRef = useRef(false);
    useEffect(() => () => { aliveRef.current = false; }, []);

    const setRunningBoth = useCallback((v: boolean) => {
        runningRef.current = v;
        setRunning(v);
    }, []);

    const follow = useCallback(async (jobId: string) => {
        if (!businessId) return;
        for (let i = 0; i < POLL_LIMIT; i++) {
            await new Promise((r) => setTimeout(r, POLL_MS));
            if (!aliveRef.current) return;
            const job = await getGoogleAiRefreshJob(businessId, jobId);
            // A dropped poll is not a failed read: keep asking.
            if (!job) continue;
            if (job === "gone") {
                // Jobs expire an hour after finishing. Nothing left to follow,
                // and the business is the place to look instead.
                setRunningBoth(false);
                setNote(null);
                void onSettled?.();
                return;
            }
            if (job.status === "failed") {
                setRunningBoth(false);
                // The backend's sentence, verbatim — it is the one that knows
                // whether the read failed or the server restarted under it.
                setNote({ tone: "warn", text: job.error || "The read did not finish." });
                // A failed read KEEPS the previous measurement. Showing it is
                // the difference between "this did not work" and "this business
                // has no Google AI figure", which are not the same claim.
                if (job.googleAi) setRow(job.googleAi);
                return;
            }
            if (job.status === "done") {
                setRunningBoth(false);
                setNote(null);
                if (job.googleAi) setRow(job.googleAi);
                void onSettled?.();
                return;
            }
        }
        if (!aliveRef.current) return;
        // NO SILENT TIMEOUT. Twenty-five minutes of watching ended; the read did
        // not. Returning to idle would say "nothing happened" over work that is
        // still going, and the next mount asks the server anyway.
        setNote({ tone: "info", text: "Still reading Google. Reload to pick it up again." });
    }, [businessId, onSettled, setRunningBoth]);

    // Ask the SERVER what is running, every mount. This is what stops a second
    // read: a reload, another tab, or another staffer all land here first.
    useEffect(() => {
        if (!enabled || !businessId) return;
        let live = true;
        void getGoogleAiRefreshState(businessId).then((state) => {
            if (!live || !state || state.status === "idle" || !state.jobId) return;
            setRunningBoth(true);
            setNote({ tone: "info", text: "Picking up the read already running." });
            void follow(state.jobId);
        });
        return () => { live = false; };
        // Once per business: the poll loop owns the rest of its own life.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, enabled]);

    const ask = useCallback(() => {
        if (runningRef.current || unavailable) return;
        setConfirmOpen(true);
    }, [unavailable]);

    const cancel = useCallback(() => setConfirmOpen(false), []);

    const run = useCallback(async () => {
        if (runningRef.current || !businessId) return;
        setNote(null);
        // The dialog STAYS OPEN until the backend answers, with its button in a
        // working state. Closing on the press and reporting the outcome
        // elsewhere is what made a refusal look like nothing happening.
        setStarting(true);
        // Before the await: a slow POST must not leave a window where a second
        // click still lands.
        setRunningBoth(true);
        const started = await startGoogleAiRefresh(businessId);
        if (!aliveRef.current) return;
        setStarting(false);
        setConfirmOpen(false);
        if (started.ok) {
            await follow(started.jobId);
            return;
        }
        if (started.kind === "absent") {
            setRunningBoth(false);
            setAbsent(true);
            return;
        }
        if (started.kind === "busy") {
            // NOT an error. A read is already in flight and the backend just
            // handed us its id — join it. Without a id there is nothing to
            // join, so say what is happening and stop.
            if (started.jobId) {
                setNote({ tone: "info", text: "A read was already running — following it." });
                await follow(started.jobId);
                return;
            }
            setRunningBoth(false);
            setNote({ tone: "info", text: started.message });
            return;
        }
        if (started.kind === "unavailable") {
            setRunningBoth(false);
            setUnavailable(started.message);
            return;
        }
        setRunningBoth(false);
        setNote({ tone: started.kind === "scanning" ? "info" : "warn", text: started.message });
    }, [businessId, follow, setRunningBoth]);

    return { running, starting, note, absent, unavailable, row, confirmOpen, ask, cancel, run };
}
